import { getCmSites, type CmSiteSummary, type SiteFreshness } from '@api/cm-sites';
import { getOrgConfig } from '@api/organization';
import { ChartBar } from '@phosphor-icons/react';

import PageHeader from '@components/common/PageHeader';
import { startOfWindow, today } from '@utils/productionWindow';
import { DataSheet, type DataSheetColumn } from '@components/ui/DataSheet';
import StatCard from '@components/ui/StatCard';
import StatusPill, { type StatusTone } from '@components/ui/StatusPill';
import { Button, Group, Text, Tooltip } from '@mantine/core';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { neutralColors, semanticColors } from '@styles/design-system';
import { useQuery } from '@tanstack/react-query';
import { type FC, useMemo } from 'react';
import { useNavigate } from 'react-router';

import { getOrganizationIdFromStorage } from '../procurementOrders/formComponents';
import { tracesAnything } from '../vendorReceive/rows';
import styles from './CmSites.module.css';
import { formatCount } from '@utils/currency';

const FRESHNESS: Record<SiteFreshness, { label: string; tone: StatusTone; hint: string }> = {
    FRESH: {
        label: 'Reporting',
        tone: 'positive',
        hint: 'Something was recorded here in the last three days.',
    },
    SLOWING: {
        label: 'Quiet',
        tone: 'warning',
        hint: 'Nothing recorded for three days or more. Worth a call before it becomes a gap.',
    },
    STALE: {
        label: 'Gone quiet',
        tone: 'critical',
        hint: 'Nothing recorded for a week or more. Whatever has happened there is not in the ledger.',
    },
    NEVER: {
        label: 'Never used',
        tone: 'critical',
        hint: 'Nothing has ever been recorded at this site. It has not started, rather than stopped.',
    },
};

/** `dd-MMM`, read against today; a last-posted date more than a year old is not the case here. */
function formatDate(value: string | null): string {
    if (!value) return 'never';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'never';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${String(date.getDate()).padStart(2, '0')} ${months[date.getMonth()]}`;
}

/**
 * Every manufacturing site the brand holds stock at.
 *
 * TWO MEASURES, AND THEY ANSWER DIFFERENT QUESTIONS. Reporting asks whether the ledger is alive
 * at all and counts every recorded row including the brand's own; adoption asks whether the
 * mandate is sticking and counts only what the site recorded itself. A brand keying everything
 * in on its manufacturers' behalf reads as perfectly healthy on the first and is failing on the
 * second — and that gap is the single most useful thing on this screen during a rollout. One
 * combined "health" number would hide it.
 *
 * WORST FIRST, from the server. The point of the list is the sites that need attention, and
 * having to sort for that every time is how a screen stops getting opened.
 */
const CmSitesContainer: FC = () => {
    const navigate = useNavigate();

    // The reader's own day, not the server's — see utils/productionWindow.
    const window = useMemo(() => {
        const day = today();
        return { day, fromDate: startOfWindow(day, 30), toDate: day };
    }, []);

    const { data, isLoading } = useQuery(['cm-sites', window.day], () =>
        getCmSites(window),
    );

    const sites = useMemo(() => data?.sites ?? [], [data]);

    const { data: orgConfig } = useQuery(
        ['org-config', getOrganizationIdFromStorage()],
        () => getOrgConfig(getOrganizationIdFromStorage() as string),
        { enabled: !!getOrganizationIdFromStorage(), staleTime: 5 * 60 * 1000 },
    );
    const tracksBatches = !orgConfig || tracesAnything(orgConfig);

    const totals = useMemo(
        () => ({
            needingAttention: sites.filter((site) => site.freshness !== 'FRESH').length,
            // Averaged only over sites that recorded something, so an idle site does not drag
            // the number down and make a working rollout look broken.
            adoption: (() => {
                const measured = sites.filter((site) => site.adoptionRatio !== null);
                if (measured.length === 0) return null;
                return (
                    measured.reduce((sum, site) => sum + (site.adoptionRatio ?? 0), 0) /
                    measured.length
                );
            })(),
            blocked: sites.reduce((sum, site) => sum + site.blocked, 0),
            producedToday: sites.reduce((sum, site) => sum + site.producedOnDay, 0),
            averageDaily: sites.reduce(
                (sum, site) => sum + site.producedDailyAverage,
                0,
            ),
        }),
        [sites],
    );

    const columns = useMemo<DataSheetColumn<CmSiteSummary>[]>(
        () => [
            {
                key: 'name',
                header: 'Site',
                width: 220,
                sticky: true,
                filterable: true,
                render: (value: string, row) => (
                    <div>
                        <Text fz={13} fw={600} c={neutralColors.textPrimary}>
                            {value}
                        </Text>
                        {row.vendorName && (
                            <Text fz={12} c={neutralColors.textMuted}>
                                {row.vendorName}
                            </Text>
                        )}
                    </div>
                ),
                csvValue: (value: string) => value,
            },
            {
                key: 'freshness',
                header: 'Reporting',
                width: 140,
                render: (value: SiteFreshness) => (
                    <Tooltip label={FRESHNESS[value].hint} multiline w={260}>
                        <span>
                            <StatusPill label={FRESHNESS[value].label} tone={FRESHNESS[value].tone} />
                        </span>
                    </Tooltip>
                ),
                csvValue: (value: SiteFreshness) => FRESHNESS[value].label,
            },
            {
                key: 'lastPostedAt',
                header: 'Last posted',
                width: 120,
                render: (value: string | null) => (
                    <Text fz={13} c={value ? neutralColors.textPrimary : neutralColors.textMuted}>
                        {formatDate(value)}
                    </Text>
                ),
                csvValue: (value: string | null) => value ?? '',
            },
            {
                key: 'adoptionRatio',
                header: 'Site-entered',
                width: 150,
                align: 'right',
                sortable: true,
                render: (value: number | null, row) => {
                    if (value === null) {
                        // "Nobody used it" and "nothing happened here" are different, and a 0%
                        // would assert the first about a site that may simply have been idle.
                        return (
                            <Text fz={13} c={neutralColors.textMuted}>
                                —
                            </Text>
                        );
                    }
                    const pct = Math.round(value * 100);
                    return (
                        <Tooltip
                            label={`${row.cmRows} of ${row.cmRows + row.brandRows} rows in the last 30 days were entered by the site itself; the rest by you on their behalf.`}
                            multiline
                            w={280}
                        >
                            <Text
                                fz={13}
                                c={
                                    pct >= 80
                                        ? semanticColors.positive.dark
                                        : pct >= 40
                                            ? semanticColors.warning.dark
                                            : semanticColors.critical.primary
                                }
                            >
                                {pct}%
                            </Text>
                        </Tooltip>
                    );
                },
                csvValue: (value: number | null) => (value === null ? '' : Math.round(value * 100)),
            },
            {
                key: 'producedOnDay',
                header: 'Made today',
                width: 130,
                align: 'right',
                sortable: true,
                /*
                 * A dash, not a zero, for a site that ran nothing.
                 *
                 * Zero and silence look identical in a total and are completely different per
                 * vendor — and "needing attention" beside it is already the column that says a
                 * site has stopped reporting. This one says what was made, so a quiet day
                 * reads as quiet rather than as an alarm in a second voice.
                 */
                render: (value: number) =>
                    value > 0 ? (
                        <Text fz={13} c={neutralColors.textPrimary}>
                            {formatCount(value)}
                        </Text>
                    ) : (
                        <Text fz={13} c={neutralColors.textMuted}>
                            —
                        </Text>
                    ),
            },
            { key: 'usable', header: 'QC approved', width: 130, align: 'right', sortable: true },
            {
                key: 'inQc',
                header: 'In QC',
                width: 100,
                align: 'right',
                sortable: true,
                render: (value: number) =>
                    value > 0 ? (
                        <Text fz={13} c={semanticColors.warning.dark}>
                            {value}
                        </Text>
                    ) : (
                        <Text fz={13} c={neutralColors.textMuted}>
                            —
                        </Text>
                    ),
            },
            {
                key: 'blocked',
                header: 'Rejected',
                width: 100,
                align: 'right',
                sortable: true,
                render: (value: number) =>
                    value > 0 ? (
                        <Text fz={13} c={semanticColors.critical.primary}>
                            {value}
                        </Text>
                    ) : (
                        <Text fz={13} c={neutralColors.textMuted}>
                            —
                        </Text>
                    ),
            },
            {
                key: 'openLines',
                header: 'Open lines',
                width: 110,
                align: 'right',
                sortable: true,
            },
            // A brand that tracks no batches never has one in QC, so this would only read "—".
            ...(tracksBatches
                ? [
                    {
                        key: 'pendingFgQc',
                        header: 'Batches in QC',
                        width: 120,
                        align: 'right',
                        sortable: true,
                        /**
                         * The brand's own queue, and a way in to it.
                         *
                         * A count that reports work and cannot be acted on sends somebody hunting for
                         * the list it came from; this lands on that site's register already filtered to
                         * what is waiting. Zero stays quiet rather than offering a link to an empty
                         * list.
                         */
                        render: (value: number, row) =>
                            value > 0 ? (
                                <Text
                                    fz={13}
                                    fw={600}
                                    c={semanticColors.warning.dark}
                                    td="underline"
                                    className={styles.qcLink}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        // navigate returns a promise in React Router v7; voided
                                        // rather than left floating, so a rejection is a deliberate
                                        // discard instead of one silently swallowed.
                                        void navigate(
                                            `/cm-sites/${row.warehouseId}?tab=batches&qc=PENDING`,
                                        );
                                    }}
                                >
                                    {value}
                                </Text>
                            ) : (
                                <Text fz={13} c={neutralColors.textMuted}>
                                    —
                                </Text>
                            ),
                    } as DataSheetColumn<CmSiteSummary>,
                  ]
                : []),
            {
                key: 'awaitingSupplier',
                header: 'Receipts needing a supplier',
                width: 150,
                align: 'right',
                sortable: true,
                // The brand's other queue: the site named no supplier because the material has
                // more than one, and somebody here has to say. Lands on the site's history
                // already narrowed to those rows.
                render: (value: number, row) =>
                    value > 0 ? (
                        <Text
                            fz={13}
                            fw={600}
                            c={semanticColors.warning.dark}
                            td="underline"
                            className={styles.qcLink}
                            onClick={(event) => {
                                event.stopPropagation();
                                void navigate(
                                    `/cm-sites/${row.warehouseId}?tab=history&supplier=waiting`,
                                );
                            }}
                        >
                            {value}
                        </Text>
                    ) : (
                        <Text fz={13} c={neutralColors.textMuted}>
                            —
                        </Text>
                    ),
            },
            {
                key: '_actions',
                header: '',
                width: 170,
                csvValue: () => '',
                render: (_value, row) => (
                    /**
                     * Straight to the entry grid, which is what the words promise. It used to
                     * open the detail generally, landing on Stock — a reading screen — so the
                     * button said "enter data" and delivered a dashboard.
                     *
                     * Kept alongside the row click because the two answer different questions.
                     * A site that is reporting is one you want to LOOK at; a site that has told
                     * you nothing is one you key in on behalf of, and for those the stock tab is
                     * empty and useless.
                     */
                    <Button
                        size="xs"
                        variant={ButtonVariant.DEFAULT}
                        onClick={(event) => {
                            // The row beneath opens the site; this opens the form.
                            event.stopPropagation();
                            void navigate(`/cm-sites/${row.warehouseId}?tab=receive`);
                        }}
                    >
                        Enter data
                    </Button>
                ),
            },
        ],
        [navigate, tracksBatches],
    );

    return (
        <div className="flex h-full w-full flex-col gap-6 page-shell">
            <PageHeader
                icon="🏭"
                title="CM Sites"
                subtitle="Where your stock sits outside your own walls, and whether anyone is telling you about it"
                actions={
                    <Button
                        size="xs"
                        variant={ButtonVariant.DEFAULT}
                        leftSection={<ChartBar size={14} />}
                        onClick={() => navigate('/production-monitor')}
                    >
                        Monitor production
                    </Button>
                }
            />

            <Group gap={16} grow align="stretch">
                <StatCard
                    label="Produced today"
                    value={formatCount(totals.producedToday)}
                    helper={
                        totals.averageDaily > 0
                            ? `${Math.round(
                                  (totals.producedToday / totals.averageDaily - 1) * 100,
                              )}% on the 30-day average`
                            : 'Across every manufacturer'
                    }
                    valueColor={neutralColors.textPrimary}
                    loading={isLoading}
                    onClick={() => navigate('/production-monitor')}
                />
                <StatCard
                    label="Needing attention"
                    value={totals.needingAttention}
                    helper="Sites quiet for three days or more"
                    valueColor={
                        totals.needingAttention > 0
                            ? semanticColors.critical.primary
                            : semanticColors.positive.dark
                    }
                    loading={isLoading}
                />
                <StatCard
                    label="Entered by sites"
                    value={totals.adoption === null ? '—' : `${Math.round(totals.adoption * 100)}%`}
                    helper="Across sites that recorded anything in 30 days"
                    valueColor={neutralColors.textPrimary}
                    loading={isLoading}
                />
                <StatCard
                    label="Rejected stock"
                    value={totals.blocked}
                    helper="Finished goods that failed QC or are past expiry, across all sites"
                    valueColor={semanticColors.critical.primary}
                    loading={isLoading}
                />
            </Group>

            <DataSheet<CmSiteSummary>
                data={sites}
                columns={columns}
                title="Sites"
                subtitle={
                    isLoading
                        ? 'Loading…'
                        : `${sites.length} site${sites.length === 1 ? '' : 's'}`
                }
                getRowKey={(row) => row.warehouseId}
                // The convention everywhere else in this portal, and what anybody tries first.
                onRowClick={(row) => navigate(`/cm-sites/${row.warehouseId}`)}
                enableSearch
                searchPlaceholder="Search sites…"
                enableDownload
                downloadFilename="cm-sites.csv"
                maxHeight="auto"
                emptyState={
                    <Text fz={13} c={neutralColors.textSecondary}>
                        No manufacturing sites yet. A site appears here once its warehouse is
                        linked to a vendor — that link is what makes you the owner of its stock.
                    </Text>
                }
            />
        </div>
    );
};

export default CmSitesContainer;
